/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import toString from 'lodash/toString';
import forEach from 'lodash/forEach';
import set from 'lodash/set';
import reduce from 'lodash/reduce';
import find from 'lodash/find';
import xor from 'lodash/xor';
import toNumber from 'lodash/toNumber';
import { localToday } from 'sarsaparilla';

import {
    ITINERARY_SINGLE_TYPE_FACILITY,
    STANDARD_TYPE_FACILITY,
    ITINERARY_TYPE_FACILITY,
} from '../constants/campground';
import { getUniqueCampsitesFromNightMap } from './availabilityGridUtil';
import { applyAllFeesWaived, getNumOccupants } from './order_details';
import {
    EMAIL_PATH,
    FIRST_NAME_PATH,
    GROUP_SIZE_BASE_PATH,
    LAST_NAME_PATH,
    NUM_OCCUPANTS_PATH,
    NUM_VEHICLES,
    NUM_VEHICLES_BASE_PATH,
    PHONE_PATH,
    POSTAL_CODE_PATH,
} from '../constants/orderDetails';
import { isMultiSiteReservation } from './reservation_utils';
import { isDateBefore, UTCStringToCalendarDate } from './dateUtils';

/**
 * check if campsite has changed (added, removed, transferred) between the previous reservation and the new reservation details
 * @param {object} previousReservation - the existing reservation
 * @param {array} newNightMap - the new night map array containing all the involved campsite ids (ITINERARY_SINGLE)
 */
export const hasCampsiteChanged = (previousReservation, newNightMap) => {
    let campsiteChanged = false;

    const prevNightMap = get(previousReservation, 'night_map', []);
    const prevCampsiteIds = getUniqueCampsitesFromNightMap(prevNightMap);
    const newCampsiteIds = getUniqueCampsitesFromNightMap(newNightMap);

    const diff = xor(prevCampsiteIds, newCampsiteIds);
    campsiteChanged = !isEmpty(diff);

    return campsiteChanged;
};

/**
 * check if the selected date range differs from the reservation's current dates.
 * `reservation.end_date` is the checkout date (last night + 1), which corresponds
 * to a selection's `actualEndDate` (its `endDate` is one day later).
 * @param {object} selection - the current selection { startDate, actualEndDate }
 * @param {object} reservation - the reservation being modified { start_date, end_date }
 */
export const haveDatesChanged = (selection, reservation) => {
    if (isNil(selection) || isNil(reservation)) {
        return false;
    }

    const selectionStart = UTCStringToCalendarDate(selection.startDate);
    const selectionEnd = UTCStringToCalendarDate(selection.actualEndDate);
    const reservationStart = UTCStringToCalendarDate(reservation.start_date);
    const reservationEnd = UTCStringToCalendarDate(reservation.end_date);

    return (
        selectionStart.compare(reservationStart) !== 0 ||
        selectionEnd.compare(reservationEnd) !== 0
    );
};

/* ============================= */
/*         REDUCER METHODS       */
/* ============================= */

/**
 * update the reservation in <pendingReservations> with <reservationId> at <path> with <value>
 * @param {object} pendingReservations - list of current pending reservations
 * @param {object} reservationsByCampsite - map of reservations groups by campsite id (options - currently supported internal only)
 * @param {string} reservationId - reservation id of the reservation to be updated
 * @param {string} path - data path for where the value should be updated (if null, entire reservation will be updated)
 * @param {string} value - new value to be updated
 * @param {string} reservationType - type of reservation (e.g. ITINERARY, ITINERARY_SINGLE, STANDARD)
 */
export const updatePendingReservation = ({
    pendingReservations,
    reservationsByCampsite,
    reservationId,
    path,
    value,
    reservationType,
}) => {
    const updatedReservations = cloneDeep(pendingReservations);

    const updateSinglePendingReservation = (resId, campsiteId) => {
        // if not path is specified, updated entire reservation
        if (isNil(path)) {
            set(updatedReservations, resId, value);
            set(updatedReservations, `${resId}.changed`, true);
        }
        // if path is defined and the new values is different from the previous value, update
        else if (!isEqual(get(pendingReservations, `${resId}.${path}`, ''), value)) {
            set(updatedReservations, `${resId}.${path}`, value);
            set(updatedReservations, `${resId}.changed`, true);
            // if group size has changed, recalculate the num_occupant value
            if (includes(path, GROUP_SIZE_BASE_PATH)) {
                const groupSize = get(
                    updatedReservations,
                    `${resId}.${GROUP_SIZE_BASE_PATH}`,
                    null
                );
                const newNumOccupant = getNumOccupants(groupSize, campsiteId);
                set(
                    updatedReservations,
                    `${resId}.${NUM_OCCUPANTS_PATH}`,
                    newNumOccupant
                );
            }
        }
    };

    // if the reservations belong to an ITINERARY site, change the `value` at the `path` for every reservations
    if (reservationType === ITINERARY_TYPE_FACILITY) {
        forEach(updatedReservations, (res, resId) => {
            const campsiteId = get(res, 'campsite_id', '');
            updateSinglePendingReservation(resId, campsiteId);
        });
    }
    // else for STANDARD and ITINERARY_SINGLE reservations and reservationsByCampsite is present (INTERNAL)
    else if (reservationsByCampsite) {
        // compare against the reservationsWithinCampsite map to check for recurring reservations that share the same campsite
        const reservation = pendingReservations[reservationId];
        const campsiteId = get(reservation, 'campsite_id', '');
        const reservationsWithinCampsite = reservationsByCampsite[campsiteId];

        /* if there are more than 1 reservations that share the same campsite,
        change the `value` at the `path` for every reservations at the campsite */
        if (
            !isNil(reservationsWithinCampsite) &&
            reservationsWithinCampsite.length >= 1
        ) {
            forEach(reservationsWithinCampsite, (res) => {
                const recurringResId = res.reservation_id;
                updateSinglePendingReservation(recurringResId);
            });
        }
        // change the `value` at the `path` for the reservation with specified `reservationId`
        else {
            updateSinglePendingReservation(reservationId);
        }
    }
    // else for STANDARD and ITINERARY_SINGLE reservations and reservationsByCampsite is NOT present (EXTERNAL)
    else {
        updateSinglePendingReservation(reservationId);
    }
    return updatedReservations;
};

/**
 * Initialize the group size request structure for `reservation` for future modification
 * @param {array} newReservations - list of incoming new reservation details
 * @param {object} reservation - reservation to be populated with default group size structure
 * @param {string} defaultGroupSize - default group size on pending reservation initialization
 * @param {string} reservationType - - type of reservation (e.g. ITINERARY, ITINERARY_SINGLE, STANDARD)
 */
export const initializeGroupSize = (
    newReservations,
    reservation,
    defaultGroupSize,
    reservationType
) => {
    const groupSize = {};
    const hasMultipleSites = isMultiSiteReservation(reservation.night_map);

    // ITINERARY: initialize group size for all campsites reserved at facility since the reservations will be combined into one in the UI
    if (reservationType === ITINERARY_TYPE_FACILITY) {
        set(groupSize, 'group_size', defaultGroupSize);
        forEach(newReservations, (res) => {
            const campsiteId = res.campsite_id;
            /* if campsite id is defined, initialize a group_size item (campsite id is
            undefined if the reservation is rental, which doesn't need group size) */
            if (campsiteId) {
                set(groupSize, `group_size-${res.campsite_id}`, defaultGroupSize);
            }
        });
    }
    // ITINERARY_SINGLE: get all the unique campsites from the single reservation's nightmap, and initialize group size for all of them
    else if (
        reservation.facility_type === ITINERARY_SINGLE_TYPE_FACILITY ||
        hasMultipleSites
    ) {
        const uniqueCampsites = getUniqueCampsitesFromNightMap(reservation.night_map);

        set(groupSize, 'group_size', defaultGroupSize);
        forEach(uniqueCampsites, (campsiteId) => {
            set(groupSize, `group_size-${campsiteId}`, defaultGroupSize);
        });
    }
    // STANDARD: will only render one group size per reservation, so 'is_group_size_same' is always gonna be true
    else {
        set(groupSize, 'group_size', defaultGroupSize);
        set(groupSize, 'is_group_size_same', 'true');
    }

    return groupSize;
};

/**
 * Initialize the number of vehicles request structure for `reservation` for future modification
 * @param {array} newReservations - list of incoming new reservation details
 * @param {object} reservation - reservation to be populated with default number of vehicles structure
 * @param {string} defaultNumVehicles - default number of vehicles on pending reservation initialization
 */
const initializeNumVehicles = (newReservations, reservation, defaultNumVehicles) => {
    const numVehicles = {};
    const hasMultipleSites = isMultiSiteReservation(reservation.night_map);

    if (hasMultipleSites) {
        const uniqueCampsites = getUniqueCampsitesFromNightMap(reservation.night_map);

        set(numVehicles, 'num_vehicles', defaultNumVehicles);
        forEach(uniqueCampsites, (campsiteId) => {
            set(numVehicles, `num_vehicles-${campsiteId}`, defaultNumVehicles);
        });
        return numVehicles;
    }

    set(numVehicles, 'num_vehicles', defaultNumVehicles);
    set(numVehicles, 'is_num_vehicles_same', 'true');
    return numVehicles;
};

/**
 * set the initial default reservation option value for a pending reservation
 * @param {object} pendingReservation - a single pending reservation to be set
 * @param {string} source - specifies the source of the potential reservation modification ('Field Sales', 'Call Center', or 'Web')
 */
const initializeReservationOptions = (pendingReservation, source) => {
    const nightMap = get(pendingReservation, 'night_map', []);
    const groupSize = get(
        pendingReservation,
        'site_occupant.custom_order_details.group_size',
        {}
    );
    const isItinerarySingle = isEqual(
        pendingReservation.facility_type,
        ITINERARY_SINGLE_TYPE_FACILITY
    );
    const processedNightMap = reduce(
        nightMap,
        (acc, value, key) => {
            const campsiteId = value.campsite_id;
            acc[key] = {
                ...value,
                num_people: toNumber(
                    groupSize[`group_size-${campsiteId}`] || groupSize.group_size || 0
                ),
            };
            return acc;
        },
        {}
    );

    // Initialize the supplemental equipment fields
    const supplementalSelections = get(pendingReservation, 'supplemental_selection', []);

    const reservationOptions = {
        is_itinerary_single: isItinerarySingle,
        night_map: processedNightMap,
        supplemental_selection: supplementalSelections,
        source,
    };

    return reservationOptions;
};

/**
 * Initialize a pendingReservation object with reservation id as keys and reservation details as value.
 * This is to help easier access to specific reservations for future modification
 * @param {array} newReservations - list of incoming new reservation details
 * @param {object} pendingReservations - list of pending reservations in store to be updated
 * @param {number} [sessionGroupSize] - group size in session storage
 * @param {string} reservationType - type of reservation (e.g. ITINERARY, ITINERARY_SINGLE, STANDARD)
 * @param {string} source - the source to send in reservation_options ('Call Center', 'Field Sales', or 'Web')
 * @param account
 */
export const initializePendingReservations = ({
    newReservations,
    pendingReservations,
    sessionGroupSize,
    reservationType,
    source,
    account = null,
}) => {
    const isUpdatingRentalRes = !isNil(get(newReservations, '[0].resource_id', null));

    /* depending on if we're initializing a list of campsite res or rentals res,
    we preserve the existing mapping of the opposite type of reservations */
    const preservedReservations = reduce(
        pendingReservations,
        (result, res, resId) => {
            if (isUpdatingRentalRes && isNil(res.resource_id)) {
                set(result, resId, res);
            } else if (!isUpdatingRentalRes && !isNil(res.resource_id)) {
                set(result, resId, res);
            }
            return result;
        },
        {}
    );

    // initialize every new reservation item with group size and reservation options
    const newPendingReservations = {};
    forEach(newReservations, (reservation) => {
        set(newPendingReservations, reservation.reservation_id, reservation);

        // if group size data doesn't exist, initialize the group size data structure and num_occupants for future modification
        const groupSize = get(reservation, GROUP_SIZE_BASE_PATH, null);
        const numOccupants = get(reservation, NUM_OCCUPANTS_PATH, 0);

        if (!groupSize) {
            let defaultGroupSize = 0;
            // if num_occupant already existing in the initialized data (e.g. camp 4), default group size if num_occupant
            if (numOccupants !== 0) {
                defaultGroupSize = numOccupants;
            }
            // else if group size session storage exists, use it as default value (e.g. for itinerary when users pre-fill group size)
            else if (!isNil(sessionGroupSize)) {
                //
                defaultGroupSize = sessionGroupSize;
            }

            const initGroupSizeObj = initializeGroupSize(
                newReservations,
                reservation,
                toString(defaultGroupSize),
                reservationType
            );

            set(
                newPendingReservations,
                `${reservation.reservation_id}.${GROUP_SIZE_BASE_PATH}`,
                initGroupSizeObj
            );
            set(
                newPendingReservations,
                `${reservation.reservation_id}.${NUM_OCCUPANTS_PATH}`,
                defaultGroupSize
            );
        }

        // if number of vehicles size data doesn't exist, initialize the number of vehicles data structure and num_occupants for future modification
        const numVehiclesObj = get(reservation, NUM_VEHICLES_BASE_PATH, null);
        const numVehicles = get(reservation, NUM_VEHICLES, 0);

        if (!numVehiclesObj) {
            let defaultNumVehicles = 0;
            if (numVehicles !== 0) {
                defaultNumVehicles = numVehicles;
            }

            const initNumVehiclesObj = initializeNumVehicles(
                newReservations,
                reservation,
                toString(defaultNumVehicles)
            );
            set(
                newPendingReservations,
                `${reservation.reservation_id}.${NUM_VEHICLES_BASE_PATH}`,
                initNumVehiclesObj
            );
            set(
                newPendingReservations,
                `${reservation.reservation_id}.${NUM_VEHICLES}`,
                defaultNumVehicles
            );
        }

        // If there's no primary occupant set and there's an account set the primary occupant from the account info
        const primaryOccupantEmail = get(reservation, EMAIL_PATH, '');
        if (account && isEmpty(primaryOccupantEmail)) {
            const accountPhone = get(
                account,
                'cell_phone',
                get(account, 'home_phone', '')
            );
            const accountZip = get(account, 'home_address.zip_code', '');

            set(
                newPendingReservations,
                `${reservation.reservation_id}.${EMAIL_PATH}`,
                account.email
            );
            set(
                newPendingReservations,
                `${reservation.reservation_id}.${FIRST_NAME_PATH}`,
                account.first_name
            );
            set(
                newPendingReservations,
                `${reservation.reservation_id}.${LAST_NAME_PATH}`,
                account.last_name
            );
            set(
                newPendingReservations,
                `${reservation.reservation_id}.${PHONE_PATH}`,
                accountPhone
            );
            set(
                newPendingReservations,
                `${reservation.reservation_id}.${POSTAL_CODE_PATH}`,
                accountZip
            );
        }

        // Initialize reservation options
        const reservationOptions = initializeReservationOptions(reservation, source);
        set(
            newPendingReservations,
            `${reservation.reservation_id}.reservation_options`,
            reservationOptions
        );
    });

    // concat the new reservation list with the preserved opposite list of reservations
    return { ...preservedReservations, ...newPendingReservations };
};

/**
 * group reservations by campsite id
 * @param {array} reservations - list of reservation objects
 */
export const initializeReservationsByCampsites = (reservations) => {
    return reduce(
        reservations,
        (result, reservation) => {
            const campsiteId = reservation.campsite_id;
            const newResult = cloneDeep(result);

            if (result[campsiteId]) {
                newResult[campsiteId] = sortBy(
                    [...result[campsiteId], reservation],
                    ['start_date', 'campsite_name']
                );
            } else {
                newResult[campsiteId] = [reservation];
            }

            return newResult;
        },
        {}
    );
};

/**
 * Update the state for the unprocessed validation error object with the new list of errors from a form section for a reservation.
 * We keep track of the "updateUnprocessedValidationErrors" object in state because the structure makes it easier and faster to search and update
 * @param {object} state - redux state
 * @param {array} errors - a list of errors for the form with `formKey` at reservation with `reservationId`
 * @param {string} reservationId - reservation id associated to `errors`
 * @param {string} formKey - form key for the form associated to `errors`
 */
export const updateUnprocessedValidationErrors = (
    pendingReservationsErrorsUnprocessed,
    errors,
    reservationId,
    formKey
) => {
    const unprocessedErrors = { ...pendingReservationsErrorsUnprocessed };
    const sectionId = !isEmpty(reservationId) ? reservationId : 'general';

    // if form with formKey's error list is empty...
    if (isEmpty(errors)) {
        // ... clear the formKey level's error list
        const formErrors = get(unprocessedErrors, `${sectionId}.${formKey}`, null);
        if (formErrors) {
            delete unprocessedErrors[sectionId][formKey];
        }

        // ... clear the reservaton level's error list if it's now empty
        const sectionErrors = get(unprocessedErrors, `${sectionId}`, null);
        if (sectionErrors && isEmpty(sectionErrors)) {
            delete unprocessedErrors[sectionId];
        }
    }
    // if new error list is NOT empty, set errors at the correct reservation and form path
    else {
        set(unprocessedErrors, `${sectionId}.${formKey}`, errors);
    }

    return unprocessedErrors;
};

/**
 * format the 'unprocessedErrors' object to a structure acceptable by sarsa's FormValidationAlert component
 * @param {object} state - redux state
 * @param {object} unprocessedErrors - an updated unprocessed validation errors object
 */
export const getUpdatedErrors = (
    pendingReservations,
    unprocessedErrors,
    isRentalOnly
) => {
    // re-structure the 'unprocessedErrors' object to merge errors in all form sections per reservation into one single error array
    const finalResult = reduce(
        unprocessedErrors,
        (result, formSection, reservationId) => {
            const tempFinalResult = { ...result };
            const combinedErrorsList = [];

            // error section key mapping corresponding error objects; also serves as the display name in the UI
            let errorSectionKey;

            const matchedReservation = get(pendingReservations, reservationId, null);
            const facilityType = get(
                matchedReservation,
                'facility_type',
                STANDARD_TYPE_FACILITY
            );

            // if reservationId cannot be found, we assume the error section is 'General'
            if (!matchedReservation) {
                errorSectionKey = 'General';
            }
            // if order details is RENTAL-ONLY, use the resource name as error key and display name
            else if (isRentalOnly) {
                errorSectionKey = get(
                    pendingReservations,
                    `${reservationId}.resource_type`,
                    'Equipment'
                );
            }
            // if reservation facility type is ITINERARY or ITINERARY_SINGLE, use the facility's name info as error key and display name
            else if (includes(facilityType, ITINERARY_TYPE_FACILITY)) {
                errorSectionKey = get(
                    pendingReservations,
                    `${reservationId}.extra_info.location`,
                    'General'
                );
            }
            // for all other facility types (STANDARD), use the campite name info as the error key and display name
            else {
                const siteName = get(
                    pendingReservations,
                    `${reservationId}.campsite_name`,
                    ''
                );
                const loopName = get(
                    pendingReservations,
                    `${reservationId}.campsite_loop`,
                    ''
                );
                errorSectionKey = `Site: ${siteName}, Loop: ${loopName}`;
            }

            // push all the errors at the current form into the combined error array for the error section
            forEach(formSection, (formErrors) => {
                combinedErrorsList.push(...formErrors);
            });

            tempFinalResult[errorSectionKey] = combinedErrorsList;

            return tempFinalResult;
        },
        {}
    );

    return finalResult;
};

/**
 * get all the violations from the pending reservations and put them in an array
 * @param {object} pendingReservations - map of pending reservations
 */
export const getViolations = (pendingReservations) => {
    const violations = [];

    forEach(pendingReservations, (reservation) => {
        if (!isEmpty(reservation.violations) && !isNil(reservation.violations)) {
            forEach(reservation.violations, (violation) => {
                violations.push(
                    `Site: ${reservation.campsite_name}, Loop: ${reservation.campsite_loop}: ${violation}`
                );
            });
        }
    });

    return [...new Set(violations)];
};

/**
 * initialize the price details object using price data from the initial reservation details
 * @param {object} pendingReservations - a map of reservation ids to reservations
 */
export const initializePriceDetails = (pendingReservations) => {
    const priceDetails = {};

    forEach(pendingReservations, (reservation, reservationId) => {
        priceDetails[reservationId] = applyAllFeesWaived(
            {
                line_items: reservation.line_items,
                subtotal: parseFloat(reservation.subtotal, 10),
                total_cost: parseFloat(reservation.total_cost, 10),
            },
            reservation
        );
    });

    return priceDetails;
};

/**
 * check if all the reservations in the order details store only includes rentals reservations
 * @param {array} reservations - all reservations (campsite and rentals) currently in store
 */
export const checkIfRentalOnly = (reservations) => {
    if (isEmpty(reservations)) {
        return false;
    }

    // itinerary is not rental-only if at least one reservation doesn't have reservation id
    const nonRentalRes = find(reservations, (reservation) =>
        isNil(reservation.resource_id)
    );
    return isNil(nonRentalRes);
};

/**
 * Check if reservation is a past reservation
 * We need to do this manual end_date check in addition to the 'is_historical' flag, because if the user
 * never checked out/cancel/no show a reservation, it'll still take up to 60 days for our service to transfer
 * the reservation to an archive table and mark it as 'is_historical: true'
 * @param {object} reservation - the reservation to be checked
 * @param {boolean} isHistorical - if the reservation is a historical reservation
 */
export const checkIsPastReservation = (reservation, isHistorical) => {
    const resStartDate = reservation.start_date;
    const resEndDate = UTCStringToCalendarDate(reservation.end_date);

    const isStartDateValid = !isEqual(resStartDate, '0001-01-01T00:00:00Z');

    const hasEndDatePassed = isDateBefore(resEndDate, localToday);

    // if 'is_historical' flag is true OR if the reservation end date has already passed, the reservation is a past reservation
    return isHistorical || (isStartDateValid && hasEndDatePassed);
};
